import React, { useEffect } from "react";
import moment from "moment-timezone";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader } from "lucide-react";
import { useListCustomers } from "@/lib/api/customer";
import { useListMarketZones, useListLocations } from "@/lib/api/generalContract";
import { useListTraders } from "@/lib/api/trader";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useFormField } from "@/components/ui/form";
import { CashSaleStatus, PrStatus, RiskCashSale } from "@/lib/api/risk/cashSales/general";
import { AcceptedCommodity } from "@/lib/api/risk/profiles";
import { CreateCashSaleSchema, UpdateCashSaleSchema } from "@/lib/api/risk/manualEntries";
import { SelectOptionItem } from "@/lib/models/ui";
import { formulateOptionMonthsWithOlder } from "@/lib/api/common";
import { generateContractTypesWith as generateGeneralContractTypesWith } from "@/lib/api/generalContract";
import { CommonDestinationTimezone } from "@/lib/utils";
import { safeParseDecimal } from "@/lib/api/common";
import { FormInput, FormCombobox, FormTextarea } from "./FormComponents";

const RiskContractCashSalesTypeOptions = [
    { value: 'DirectSale', label: 'Direct Sale' },
    { value: 'OTC', label: 'OTC' },
    ...generateGeneralContractTypesWith({ }).map(item => ({ value: item, label: item })),
];

export type CreateFormData = z.infer<typeof CreateCashSaleSchema>;
export type UpdateFormData = z.infer<typeof UpdateCashSaleSchema>;
export type CashSalesFormData = CreateFormData | UpdateFormData;

interface CashSalesFormPropsBase {
    onCancel?: () => void;
    showCancelButton?: boolean;
    isSubmitting?: boolean;
}

interface CreateFormProps extends CashSalesFormPropsBase {
    mode: "create";
    initialData?: RiskCashSale | null;
    onSubmit: (data: CreateFormData) => Promise<void>;
}

interface UpdateFormProps extends CashSalesFormPropsBase {
    mode: "update";
    initialData: RiskCashSale | null;
    onSubmit: (data: UpdateFormData) => Promise<void>;
}

type CashSalesFormProps = CreateFormProps | UpdateFormProps;

export function CashSalesForm(props: CashSalesFormProps) {
    const [customerIds, _setCustomerIds] = useQueryParamArray("customer_ids");
    const { mode, initialData, onSubmit, onCancel, showCancelButton = true, isSubmitting = false } = props;
    
    // Data fetching hooks
    const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
    const listTradersRes = useListTraders();
    const locationsResult = useListLocations();
    const marketZonesResult = useListMarketZones();

    // Use the appropriate schema based on mode
    const formSchema = mode === "create" ? CreateCashSaleSchema : UpdateCashSaleSchema;

    // Create form with proper typing
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: "onChange",
        defaultValues: {
            status: initialData?.status as CashSaleStatus || CashSaleStatus.OpenNotPrinted,
            contract_type: initialData?.contract_type || "",
            contract_number: initialData?.contract_number || "",
            contract_release: initialData?.contract_release || "1",
            location_id: initialData?.location_id || "",
            customer_id: initialData?.customer_id || customerIds?.[0] || "",
            market_zone_id: initialData?.market_zone_id || "",
            trader_id: initialData?.trader_id || "",

            destination: initialData?.destination || "",
            washed_gain_loss: initialData?.washed_gain_loss || "",

            add_date: initialData?.add_date ? moment.tz(initialData.add_date, CommonDestinationTimezone).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
            start_date: initialData?.start_date ? moment.tz(initialData.start_date, CommonDestinationTimezone).format("YYYY-MM-DD") : "",
            end_date: initialData?.end_date ? moment.tz(initialData.end_date, CommonDestinationTimezone).format("YYYY-MM-DD") : "",
            note: initialData?.note || "",
            commodity: initialData?.commodity as AcceptedCommodity,
            crop_year: initialData?.crop_year,
            option_month: initialData?.option_month || "",
            loads: initialData?.loads || "",
            quantity: initialData?.quantity?.toString() || "",
            scheduled_quantity: initialData?.scheduled_quantity?.toString() || "",
            balance: initialData?.balance?.toString() || "",
            futures: initialData?.futures?.toString() || "",
            basis: initialData?.basis?.toString() || "",
            price: initialData?.price?.toString() || "",
        },
    });


    const futuresValue = form.watch('futures');
    const basisValue = form.watch('basis');
    const contractType = form.watch('contract_type');
    
    const isDirectSale = contractType === 'DirectSale';

    useEffect(() => {
        if (futuresValue && basisValue && !isDirectSale) {
            const result = safeParseDecimal({})(futuresValue || 0).plus(safeParseDecimal({})(basisValue || 0)).toNumber();
            form.setValue('price', result.toString());
        }
    }, [futuresValue, basisValue, form.setValue, isDirectSale]);

    useEffect(() => {
        if (contractType === 'DirectSale') {
            form.setValue('contract_number', 'Direct Sale');
        }
    }, [contractType, form.setValue]);

    // Reset form when initialData changes
    useEffect(() => {
        if (initialData) {
            form.reset({
                status: initialData.status as CashSaleStatus || undefined,
                contract_type: initialData.contract_type || undefined,
                contract_number: initialData.contract_number || undefined,
                contract_release: initialData.contract_release || undefined,
                location_id: initialData.location_id || undefined,
                customer_id: initialData?.customer_id || customerIds?.[0] || "",
                market_zone_id: initialData.market_zone_id || undefined,
                trader_id: initialData.trader_id || undefined,

                destination: initialData?.destination || "",
                washed_gain_loss: initialData?.washed_gain_loss || "",

                add_date: initialData.add_date ? moment.tz(initialData.add_date, CommonDestinationTimezone).format("YYYY-MM-DD") : undefined,
                start_date: initialData.start_date ? moment.tz(initialData.start_date, CommonDestinationTimezone).format("YYYY-MM-DD") : undefined,
                end_date: initialData.end_date ? moment.tz(initialData.end_date, CommonDestinationTimezone).format("YYYY-MM-DD") : undefined,
                note: initialData.note || undefined,
                commodity: initialData.commodity as AcceptedCommodity || undefined,
                crop_year: initialData.crop_year || undefined,
                option_month: initialData.option_month || undefined,
                loads: initialData.loads || undefined,
                quantity: initialData.quantity?.toString() || undefined,
                scheduled_quantity: initialData.scheduled_quantity?.toString() || undefined,
                balance: initialData.balance?.toString() || undefined,
                futures: initialData.futures?.toString() || undefined,
                basis: initialData.basis?.toString() || undefined,
                price: initialData.price?.toString() || undefined,
            });
        }
    }, [initialData, form, mode]);

    // Prepare options for dropdowns
    const statusOptions: SelectOptionItem<string>[] = Object.entries(CashSaleStatus).map(([key, value]) => ({
        value: value,
        label: value.replace(/,/g, ' - ')
    }));

    const prStatusOptions: SelectOptionItem<string>[] = Object.entries(PrStatus).map(([key, value]) => ({
        value: value,
        label: value
    }));

    const commodityOptions: SelectOptionItem<string>[] = mode === "create" 
        ? Object.values(AcceptedCommodity).map(commodity => ({
            value: commodity,
            label: commodity
        }))
        : Object.values(AcceptedCommodity).map(commodity => ({
            value: commodity,
            label: commodity
        }));

    const customerOptions: SelectOptionItem<string>[] = (customersResult?.data?.data || []).map((customer: any) => ({
        value: customer.id,
        label: customer.long_name || customer.short_name || customer.id
    }));

    const traderOptions: SelectOptionItem<string>[] = (listTradersRes?.data?.data || []).map((trader: any) => ({
        value: trader.id,
        label: trader.name || trader.id
    }));

    const locationOptions: SelectOptionItem<string>[] = (locationsResult?.data?.data || []).map((location: any) => ({
        value: location.id,
        label: location.long_name
    }));

    const marketZoneOptions: SelectOptionItem<string>[] = (marketZonesResult?.data?.data || []).map((zone: any) => ({
        value: zone.id,
        label: zone.dtn_market_zone,
    }));

    const handleSubmit = form.handleSubmit((data) => {
        if (mode === "create") {
            return onSubmit(data as CreateFormData);
        } else {
            return onSubmit(data as UpdateFormData);
        }
    });

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contract Information - only for create mode */}
                    <div>
                        <h3 className="text-lg font-medium mb-4">Contract Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <FormField
                                control={form.control}
                                name="contract_type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Contract Type *</FormLabel>
                                        <FormControl>
                                            <FormCombobox
                                                dwimOnChange
                                                isModal
                                                options={RiskContractCashSalesTypeOptions}
                                                value={field.value || ""}
                                                onValueChange={field.onChange}
                                                placeholder="Select Contract Type"
                                                popoverClassName="w-full min-w-[300px]"
                                                className="w-full"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />


                                <FormField
                                    control={form.control}
                                    name="commodity"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Commodity *</FormLabel>
                                            <FormControl>
                                                <FormCombobox
                                                    dwimOnChange
                                                    isModal
                                                    options={commodityOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select commodity"
                                                    className="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="crop_year"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Crop Year *</FormLabel>
                                            <FormControl>
                                                <FormInput
                                                    type="number"
                                                    {...field}
                                                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                            <FormField
                                control={form.control}
                                name="contract_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Contract Number</FormLabel>
                                        <FormControl>
                                            <FormInput {...field} value={field.value || ""} placeholder="Enter contract number" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        </div>
                    </div>


                    <>
                        {/* Parties Information - only for create mode */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">Parties</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <FormField
                                    control={form.control}
                                    name="customer_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Customer *</FormLabel>
                                            <FormControl>
                                                <FormCombobox
                                                    dwimOnChange
                                                    isModal
                                                    options={customerOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select customer"
                                                    className="w-full"
                                                    popoverClassName="w-full min-w-[300px]"
                                                    disabled={customersResult.isLoading}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="trader_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Trader *</FormLabel>
                                            <FormControl>
                                                <FormCombobox
                                                    dwimOnChange
                                                    isModal
                                                    options={traderOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select trader"
                                                    className="w-full"
                                                    disabled={listTradersRes.isLoading}
                                                    popoverClassName="w-full min-w-[300px]"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="location_id"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Location</FormLabel>
                                                <FormControl>
                                                    <FormCombobox
                                                        dwimOnChange
                                                        isModal
                                                        options={locationOptions}
                                                        value={field.value || ""}
                                                        onValueChange={field.onChange}
                                                        placeholder="Select location (optional)"
                                                        popoverClassName="w-full min-w-[300px]"
                                                        className="w-full"
                                                        disabled={locationsResult.isLoading}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="market_zone_id"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Market Zone *</FormLabel>
                                                <FormControl>
                                                    <FormCombobox
                                                        dwimOnChange
                                                        isModal
                                                        options={marketZoneOptions}
                                                        value={field.value || ""}
                                                        onValueChange={field.onChange}
                                                        placeholder="Select market zone"
                                                        popoverClassName="w-full min-w-[300px]"
                                                        className="w-full"
                                                        disabled={marketZonesResult.isLoading}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="destination"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Destination</FormLabel>
                                                <FormControl>
                                                    <FormInput
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter destination"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </div>
                        </div>


                        {/* Dates - only for create mode */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">Dates</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="add_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Add Date *</FormLabel>
                                            <FormControl>
                                                <FormInput type="date" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                    <FormField
                                        control={form.control}
                                        name="start_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Start Date</FormLabel>
                                                <FormControl>
                                                    <FormInput type="date" {...field} value={field.value || ""} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="end_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>End Date</FormLabel>
                                                <FormControl>
                                                    <FormInput type="date" {...field} value={field.value || ""} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                            </div>
                        </div>

                        {/* Quantities and Pricing - only for create mode */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">Quantities and Pricing</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="quantity"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Quantity *</FormLabel>
                                            <FormControl>
                                                <FormInput
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="Enter quantity"
                                                    type="number"
                                                    step="0.01"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="scheduled_quantity"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Scheduled Quantity</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter scheduled quantity"
                                                        type="number"
                                                        step="0.01"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="balance"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Balance</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter balance"
                                                        type="number"
                                                        step="0.01"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {!isDirectSale && (
                                    <>
                                        <FormField
                                            control={form.control}
                                            name="futures"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Futures</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            value={field.value || ""}
                                                            placeholder="Enter futures"
                                                            type="number"
                                                            step="0.01"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="basis"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Basis</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            value={field.value || ""}
                                                            placeholder="Enter basis"
                                                            type="number"
                                                            step="0.01"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )}

                                <FormField
                                    control={form.control}
                                    name="price"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Price</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="Enter price"
                                                    type="number"
                                                    step="0.01"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="washed_gain_loss"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Washed Gain/Loss</FormLabel>
                                                <FormControl>
                                                    <FormInput
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter washed gain/loss"
                                                        type="number"
                                                        step="0.01"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="option_month"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Option Month</FormLabel>
                                                <FormControl>
                                                    <FormCombobox
                                                        dwimOnChange
                                                        isModal
                                                        options={formulateOptionMonthsWithOlder(field.value)}
                                                        value={field.value || ""}
                                                        onValueChange={field.onChange}
                                                        placeholder="Select option month"
                                                        className="w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {!isDirectSale && (
                                    <FormField
                                        control={form.control}
                                        name="loads"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Loads</FormLabel>
                                                <FormControl>
                                                    <FormInput
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Enter loads"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                <FormField
                                    control={form.control}
                                    name="note"
                                    render={({ field }) => (
                                        <FormItem className="">
                                            <FormLabel>Note</FormLabel>
                                            <FormControl>
                                                <FormTextarea
                                                    {...field}
                                                    value={field.value || ""}
                                                    placeholder="Enter any additional notes"
                                                    className="resize-none"
                                                    rows={3}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {!!isDirectSale && (
                                    <>
                                        <FormField
                                            control={form.control}
                                            name="futures"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Futures</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            value={field.value || ""}
                                                            placeholder="Enter futures"
                                                            type="number"
                                                            step="0.01"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="basis"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Basis</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            value={field.value || ""}
                                                            placeholder="Enter basis"
                                                            type="number"
                                                            step="0.01"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </>

                <div className="flex justify-end gap-2 pt-4">
                    {showCancelButton && onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                {mode === "create" ? "Creating..." : "Updating..."}
                            </>
                        ) : (
                            mode === "create" ? "Create Cash Sale" : "Update"
                        )}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
